import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown, Search } from "lucide-react";

import { cn, getModelThumbnailUrl } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ModelThumbnailFallback } from "@/components/common/ModelThumbnailFallback";
import { useLLMDropdown } from "@/features/llm-selection/hooks";
import type { LLMModel } from "@/api/models/api-types";

interface ModelPickerFieldProps {
  /** Form'da tutulan seçili model adı (LLMModel.name) */
  value: string;
  onChange: (modelName: string) => void;
  placeholder: string;
  hasError?: boolean;
}

/**
 * Chat ekranındaki model seçici ile aynı deneyim: aramalı, sonsuz kaydırmalı liste.
 * Chat'ten farkı: provider_type filtresi olmadan (LLM + generative) TÜM modelleri listeler.
 */
export const ModelPickerField: React.FC<ModelPickerFieldProps> = ({
  value,
  onChange,
  placeholder,
  hasError,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<LLMModel | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    searchQuery,
    visibleModels,
    isLoading,
    isLoadingMore,
    updateSearchQuery,
    handleScroll: hookHandleScroll,
    resetVisibleItems,
  } = useLLMDropdown({ enabled: isOpen, providerType: "" });

  useEffect(() => {
    if (!isOpen) {
      updateSearchQuery("");
      resetVisibleItems();
    }
  }, [isOpen, updateSearchQuery, resetVisibleItems]);

  // Form dışarıdan sıfırlanırsa (submit sonrası reset) yerel seçim de temizlensin
  useEffect(() => {
    if (!value) setSelectedModel(null);
  }, [value]);

  const handleScroll = () => {
    if (scrollRef.current) hookHandleScroll(scrollRef.current);
  };

  const handleSelect = (model: LLMModel) => {
    setSelectedModel(model);
    onChange(model.name);
    setIsOpen(false);
  };

  const thumbnailUrl = selectedModel ? getModelThumbnailUrl(selectedModel) : null;
  const displayLabel = selectedModel?.title || selectedModel?.name || value;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-10 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-ring",
            hasError && "border-destructive",
          )}
        >
          {displayLabel ? (
            thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={displayLabel}
                className="h-5 w-5 shrink-0 rounded object-contain"
              />
            ) : (
              <ModelThumbnailFallback
                title={displayLabel}
                size="xs"
                className="h-5 w-5"
              />
            )
          ) : null}
          <span
            className={cn(
              "flex-1 truncate text-left",
              !displayLabel && "text-muted-foreground",
            )}
          >
            {displayLabel || placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-2xl border-border bg-popover p-0 shadow-lg"
      >
        <div className="p-2">
          <div className="relative">
            <Input
              autoFocus
              placeholder={t("chat.searchModel")}
              value={searchQuery}
              onChange={(event) => updateSearchQuery(event.target.value)}
              // 🐛 Fix: Radix DropdownMenu, açıkken tüm tuş vuruşlarını kendi
              // typeahead/roving-focus mantığı için Content düzeyinde dinler.
              // stopPropagation olmadan her tuş vuruşu input'un focus'unu çalıyordu.
              onKeyDown={(event) => event.stopPropagation()}
              className="h-10 border border-border bg-transparent pr-8 text-sm"
            />
            <Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="max-h-64 overflow-y-auto px-1 pb-2"
        >
          {isLoading ? (
            <div className="px-2 py-4 text-center text-sm text-muted-foreground">
              {t("common.loading")}
            </div>
          ) : visibleModels.length === 0 ? (
            <div className="px-2 py-4 text-center text-sm text-muted-foreground">
              {t("chat.noResults")}
            </div>
          ) : (
            visibleModels.map((model: LLMModel) => {
              const modelThumbnailUrl = getModelThumbnailUrl(model);
              const isSelected = model.name === value;
              return (
                <DropdownMenuItem
                  key={model._id}
                  onClick={() => handleSelect(model)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-lg py-2.5",
                    isSelected && "text-indigo-600 dark:text-indigo-400",
                  )}
                >
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded">
                    {modelThumbnailUrl ? (
                      <img
                        src={modelThumbnailUrl}
                        alt={model.title || model.name}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <ModelThumbnailFallback
                        title={model.title || model.name}
                        size="sm"
                        className="h-full w-full"
                      />
                    )}
                  </div>
                  <span className="truncate text-sm">
                    {model.title || model.name}
                  </span>
                </DropdownMenuItem>
              );
            })
          )}
          {isLoadingMore && (
            <div className="px-2 py-2 text-center text-sm text-muted-foreground">
              {t("common.loading")}...
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
